import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Search, X, Mail, Phone, MapPin, Calendar, Package, FileText, ShieldCheck, ExternalLink, Eye, Receipt, Stamp, FileSignature, User } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const abs = (u) => (u && u.startsWith("/") ? `${BACKEND}${u}` : u);

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const day = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "n/a");
const dayTime = (d) => (d ? new Date(d).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "n/a");

const DOC_META = {
  prescription: { label: "Prescription", icon: FileText, tone: "text-sky-700 dark:text-sky-300 bg-sky-500/10 border-sky-500/20" },
  vet_authorization: { label: "Vet authorization", icon: Stamp, tone: "text-violet-700 dark:text-violet-300 bg-violet-500/10 border-violet-500/20" },
  invoice: { label: "Invoice", icon: Receipt, tone: "text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border-emerald-500/20" },
  client_authorization: { label: "Client authorization", icon: FileSignature, tone: "text-amber-700 dark:text-amber-300 bg-amber-500/10 border-amber-500/20" },
};

const DRAWER_TABS = [
  { id: "overview", label: "Overview" },
  { id: "orders", label: "Orders" },
  { id: "prescriptions", label: "Prescriptions" },
  { id: "documents", label: "Documents" },
  { id: "consents", label: "Consents" },
];

const Chip = ({ tone = "", children }) => (
  <span className={`inline-flex items-center px-2.5 py-1 rounded-md border text-xs font-manrope font-medium ${tone || "bg-black/[0.03] dark:bg-white/5 text-paw-text-muted dark:text-white/40 border-paw-border dark:border-white/10"}`}>{children}</span>
);

const Field = ({ icon: Icon, label, value, mono = false, placeholder = "Not on file" }) => (
  <div className="flex items-start gap-3">
    <span className="w-8 h-8 rounded-lg bg-black/[0.03] dark:bg-white/5 flex items-center justify-center shrink-0 text-paw-text-muted dark:text-white/40">
      <Icon className="w-4 h-4" strokeWidth={1.6} />
    </span>
    <div className="min-w-0">
      <div className="font-manrope text-[11px] uppercase tracking-wider text-paw-text-muted dark:text-white/40">{label}</div>
      <div className={`font-manrope text-sm mt-0.5 break-words ${mono ? "tabular-nums" : ""} ${value ? "" : "text-paw-text-muted dark:text-white/30"}`}>{value || placeholder}</div>
    </div>
  </div>
);

const Stat = ({ label, value }) => (
  <div className="bg-black/[0.02] dark:bg-white/[0.03] border border-paw-border dark:border-white/5 rounded-xl px-4 py-3">
    <div className="font-manrope text-[11px] uppercase tracking-wider text-paw-text-muted dark:text-white/40">{label}</div>
    <div className="font-outfit font-bold text-lg mt-1 tabular-nums">{value}</div>
  </div>
);

function CustomerDrawer({ userId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    let dead = false;
    setLoading(true);
    setTab("overview");
    api.get(`/admin/customers/${userId}/profile`)
      .then(({ data }) => { if (!dead) setData(data); })
      .catch((e) => { if (!dead) { toast.error(formatApiError(e)); onClose(); } })
      .finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
  }, [userId, onClose]);

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", esc); document.body.style.overflow = ""; };
  }, [onClose]);

  const c = data?.customer;
  const s = data?.stats;
  const name = c ? `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email : "";
  const parts = c?.address_parts || {};
  const addressLine = c?.address || [parts.line1, parts.city, parts.state_province, parts.postal_code, parts.country].filter(Boolean).join(", ");

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="customer-drawer">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={onClose} />
      <aside className="relative h-full w-full max-w-3xl bg-paw-bg dark:bg-[#12141c] border-l border-paw-border dark:border-white/10 shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">

        {loading ? (
          <div className="flex-1 flex items-center justify-center gap-2 font-manrope text-sm text-paw-text-muted dark:text-white/40">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading customer…
          </div>
        ) : !data ? null : (
          <>
            {/* header */}
            <div className="px-6 pt-5 pb-4 border-b border-paw-border dark:border-white/10 bg-white dark:bg-[#1a1d27]">
              <div className="flex items-start gap-4">
                <span className="w-12 h-12 rounded-full bg-paw-accent/15 text-paw-accent flex items-center justify-center font-outfit font-bold text-lg shrink-0 uppercase">
                  {(c.first_name || c.email || "?").charAt(0)}
                </span>
                <div className="min-w-0 flex-1">
                  <h2 className="font-outfit font-bold text-xl tracking-tight capitalize truncate">{name}</h2>
                  <div className="font-manrope text-sm text-paw-text-secondary dark:text-white/60 truncate">{c.email}</div>
                  <div className="flex gap-2 mt-2.5 flex-wrap">
                    {!c.is_active && <Chip tone="bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/20">Disabled</Chip>}
                    {c.deletion_requested_at && <Chip tone="bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/20">Deletion requested</Chip>}
                    {c.is_guest && <Chip>Guest account</Chip>}
                    {c.free_shipping_credit?.available && <Chip tone="bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20">Free delivery credit</Chip>}
                    <Chip>Member since {day(c.created_at)}</Chip>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Link to={`/admin/customers/${c.id}`} className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-paw-border dark:border-white/10 hover:border-paw-accent font-manrope text-xs font-semibold transition-colors" title="Open the read-only customer dashboard">
                    <Eye className="w-3.5 h-3.5" /> View as customer
                  </Link>
                  <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-black/[0.04] dark:hover:bg-white/5 flex items-center justify-center" aria-label="Close" data-testid="drawer-close">
                    <X className="w-4.5 h-4.5" />
                  </button>
                </div>
              </div>

              <div className="flex gap-1 mt-5 -mb-4 overflow-x-auto">
                {DRAWER_TABS.map((t) => {
                  const n = t.id === "orders" ? s.orders_total : t.id === "prescriptions" ? s.prescriptions_total : t.id === "documents" ? s.documents_total : t.id === "consents" ? (data.consents || []).length : 0;
                  return (
                    <button key={t.id} onClick={() => setTab(t.id)} data-testid={`drawer-tab-${t.id}`}
                      className={`px-3.5 py-2.5 font-manrope text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${tab === t.id ? "border-paw-accent text-paw-accent" : "border-transparent text-paw-text-secondary dark:text-white/50 hover:text-paw-text dark:hover:text-white"}`}>
                      {t.label}{n > 0 ? <span className="ml-1.5 text-xs opacity-60 tabular-nums">{n}</span> : null}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* body */}
            <div className="flex-1 overflow-y-auto px-6 py-6">

              {tab === "overview" && (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <Stat label="Orders" value={s.orders_total} />
                    <Stat label="Paid" value={s.orders_paid} />
                    <Stat label="Lifetime spend" value={money(s.spend_total)} />
                    <Stat label="Quotes" value={s.estimates_total} />
                  </div>

                  <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl p-5">
                    <h3 className="font-outfit font-semibold text-sm mb-4">Contact</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field icon={Mail} label="Email" value={c.email} />
                      <Field icon={Phone} label="Phone" value={c.phone} />
                      <Field icon={Calendar} label="Date of birth" value={c.dob ? day(c.dob) : ""} />
                      <Field icon={User} label="Last sign-in" placeholder="No sign-in recorded yet" value={c.last_login_at ? `${dayTime(c.last_login_at)}${c.login_count ? ` · ${c.login_count} sign-in${c.login_count === 1 ? "" : "s"}` : ""}` : ""} />
                    </div>
                  </div>

                  <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl p-5">
                    <h3 className="font-outfit font-semibold text-sm mb-4">Shipping address</h3>
                    <Field icon={MapPin} label="Current address" value={addressLine} />
                    {(parts.city || parts.state_province || parts.postal_code) && (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 pt-4 border-t border-paw-border dark:border-white/5">
                        <div><div className="font-manrope text-[11px] uppercase tracking-wider text-paw-text-muted dark:text-white/40">City</div><div className="font-manrope text-sm mt-0.5">{parts.city || "—"}</div></div>
                        <div><div className="font-manrope text-[11px] uppercase tracking-wider text-paw-text-muted dark:text-white/40">State</div><div className="font-manrope text-sm mt-0.5">{parts.state_province || "—"}</div></div>
                        <div><div className="font-manrope text-[11px] uppercase tracking-wider text-paw-text-muted dark:text-white/40">Postal</div><div className="font-manrope text-sm mt-0.5">{parts.postal_code || "—"}</div></div>
                        <div><div className="font-manrope text-[11px] uppercase tracking-wider text-paw-text-muted dark:text-white/40">Country</div><div className="font-manrope text-sm mt-0.5">{parts.country || "—"}</div></div>
                      </div>
                    )}
                  </div>

                  <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl p-5">
                    <h3 className="font-outfit font-semibold text-sm mb-4">Marketing permissions</h3>
                    <div className="flex gap-2 flex-wrap">
                      <Chip tone={c.marketing_email_consent ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20" : ""}>Email {c.marketing_email_consent ? "opted in" : "not opted in"}</Chip>
                      <Chip tone={c.sms_consent ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20" : ""}>SMS {c.sms_consent ? "opted in" : "not opted in"}</Chip>
                    </div>
                  </div>
                </div>
              )}

              {tab === "orders" && (
                <div className="space-y-3">
                  {data.orders.length === 0 && <p className="font-manrope text-sm text-paw-text-muted dark:text-white/40">No orders yet.</p>}
                  {data.orders.map((o) => (
                    <div key={o.order_id} className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-xl p-4" data-testid={`drawer-order-${o.order_id}`}>
                      <div className="flex items-start gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <div className="font-manrope font-semibold text-sm">{o.medication?.name || "Medication"}</div>
                          <div className="font-manrope text-xs text-paw-text-secondary dark:text-white/50 mt-1 capitalize">
                            {o.pet?.name}{o.pet?.species ? ` (${o.pet.species})` : ""} · {o.order_number} · {day(o.created_at)}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <Chip tone="bg-paw-accent/10 text-paw-accent border-paw-accent/20">{(o.status || "").replace(/_/g, " ")}</Chip>
                          <div className="font-manrope font-bold text-sm mt-1.5 tabular-nums">{money(o.amount)}</div>
                        </div>
                      </div>
                      <div className="flex gap-4 flex-wrap mt-3 pt-3 border-t border-paw-border dark:border-white/5 font-manrope text-xs text-paw-text-secondary dark:text-white/50">
                        <span>Payment: {o.payment_status || "—"}</span>
                        {o.tracking_number && <span>Tracking: {o.tracking_number}{o.carrier ? ` (${o.carrier})` : ""}</span>}
                        <span>
                          Rx: {o.rx_exhausted ? "used up" : o.rx_valid ? `valid${typeof o.rx_days_left === "number" ? `, ${o.rx_days_left} days left` : ""}` : "not valid"}
                        </span>
                        {o.refund_requested && <span className="text-amber-700 dark:text-amber-300">Refund requested</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {tab === "prescriptions" && (
                <div className="space-y-3">
                  {data.prescriptions.length === 0 && <p className="font-manrope text-sm text-paw-text-muted dark:text-white/40">No prescriptions on file.</p>}
                  {data.prescriptions.map((rx, i) => (
                    <div key={i} className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-xl p-4 flex items-start gap-3">
                      <span className="w-9 h-9 rounded-lg bg-sky-500/10 text-sky-700 dark:text-sky-300 flex items-center justify-center shrink-0"><FileText className="w-4 h-4" /></span>
                      <div className="min-w-0 flex-1">
                        <div className="font-manrope font-semibold text-sm">{rx.label}</div>
                        <div className="font-manrope text-xs text-paw-text-secondary dark:text-white/50 mt-1 capitalize">
                          {rx.pet ? `${rx.pet} · ` : ""}{rx.source === "order" ? "from order" : "from quote"} {rx.ref} · uploaded {day(rx.at)}
                        </div>
                        <div className="flex gap-2 mt-2.5 flex-wrap">
                          {rx.signed && <Chip tone="bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/20">Vet signed</Chip>}
                          {rx.exhausted ? <Chip>Used up</Chip> : rx.valid === true ? <Chip tone="bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20">Valid{typeof rx.days_left === "number" ? ` · ${rx.days_left}d left` : ""}</Chip> : null}
                        </div>
                      </div>
                      <a href={abs(rx.url)} target="_blank" rel="noreferrer" className="shrink-0 inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-paw-border dark:border-white/10 hover:border-paw-accent font-manrope text-xs font-semibold transition-colors">
                        Open <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  ))}
                </div>
              )}

              {tab === "documents" && (
                <div className="space-y-3">
                  {data.documents.length === 0 && <p className="font-manrope text-sm text-paw-text-muted dark:text-white/40">No documents on file.</p>}
                  {data.documents.map((d, i) => {
                    const meta = DOC_META[d.kind] || DOC_META.prescription;
                    const Icon = meta.icon;
                    return (
                      <div key={i} className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-xl p-4 flex items-start gap-3" data-testid={`drawer-doc-${i}`}>
                        <span className={`w-9 h-9 rounded-lg border flex items-center justify-center shrink-0 ${meta.tone}`}><Icon className="w-4 h-4" /></span>
                        <div className="min-w-0 flex-1">
                          <div className="font-manrope font-semibold text-sm">{d.label}</div>
                          <div className="font-manrope text-xs text-paw-text-secondary dark:text-white/50 mt-1">{d.sub} · {day(d.at)}</div>
                          <div className="mt-2"><Chip tone={meta.tone}>{meta.label}</Chip></div>
                        </div>
                        {d.url ? (
                          <a href={abs(d.url)} target="_blank" rel="noreferrer" className="shrink-0 inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-paw-border dark:border-white/10 hover:border-paw-accent font-manrope text-xs font-semibold transition-colors">
                            Open <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        ) : (
                          <button onClick={() => setTab("consents")} className="shrink-0 px-3 h-9 rounded-lg border border-paw-border dark:border-white/10 hover:border-paw-accent font-manrope text-xs font-semibold transition-colors">
                            View record
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {tab === "consents" && (
                <div className="space-y-3">
                  {(data.consents || []).length === 0 && <p className="font-manrope text-sm text-paw-text-muted dark:text-white/40">No consent records stored for this account.</p>}
                  {(data.consents || []).map((k) => (
                    <div key={k.consent_id} className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-xl p-5">
                      <div className="flex items-start gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <div className="font-manrope font-semibold text-sm capitalize">{(k.context || "signup").replace(/_/g, " ")}</div>
                          <div className="font-manrope text-xs text-paw-text-secondary dark:text-white/50 mt-1">Accepted {dayTime(k.accepted_at_utc)}</div>
                        </div>
                        <div className="flex gap-2 flex-wrap shrink-0">
                          <Chip>Terms v{k.terms_version}</Chip>
                          <Chip>Privacy v{k.privacy_version}</Chip>
                          <Chip>Client auth v{k.client_authorization_version}</Chip>
                        </div>
                      </div>
                      {k.items?.length > 0 && (
                        <ul className="mt-4 pt-4 border-t border-paw-border dark:border-white/5 space-y-2">
                          {k.items.map((it) => (
                            <li key={it.key} className="flex items-start gap-2.5 font-manrope text-xs">
                              <ShieldCheck className={`w-4 h-4 shrink-0 mt-px ${it.accepted ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`} />
                              <span className={it.accepted ? "text-paw-text-secondary dark:text-white/60" : "text-red-700 dark:text-red-300"}>{it.label}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </aside>
    </div>
  );
}

export default function AdminCustomers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers((data || []).filter((u) => !u.role || u.role === "customer"));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const closeDrawer = useCallback(() => setOpenId(null), []);

  const needle = q.trim().toLowerCase();
  const filtered = !needle ? users : users.filter((u) =>
    [`${u.first_name || ""} ${u.last_name || ""}`, u.email, u.phone].filter(Boolean).some((v) => String(v).toLowerCase().includes(needle))
  );

  return (
    <div className="px-3 sm:px-6 lg:px-10 py-5 sm:py-8 lg:py-10 max-w-7xl" data-testid="admin-customers">
      <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
        <div>
          <p className="font-manrope text-xs uppercase tracking-widest text-paw-text-muted dark:text-white/40 mb-2">Access Control</p>
          <h1 className="font-outfit font-bold text-2xl sm:text-3xl tracking-tight">Customers</h1>
          <p className="font-manrope text-sm text-paw-text-secondary dark:text-white/50 mt-2">Open a customer to see their documents, prescriptions, orders and contact details.</p>
        </div>
        <div className="flex items-center gap-2 h-10 px-3 rounded-lg bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/10 w-full sm:w-80">
          <Search className="w-4 h-4 text-paw-text-muted dark:text-white/40 shrink-0" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or phone" data-testid="customers-search"
            className="bg-transparent border-none outline-none font-manrope text-sm w-full" />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-paw-text-muted dark:text-white/40 font-manrope text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl overflow-x-auto">
          <table className="w-full">
            <thead className="bg-black/[0.02] dark:bg-white/[0.02] border-b border-paw-border dark:border-white/5">
              <tr className="text-left">
                {["Name", "Email", "Phone", "Status", "Member since", ""].map((h, i) => (
                  <th key={i} className="px-5 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-10 text-center font-manrope text-sm text-paw-text-muted dark:text-white/40">
                  {needle ? "No customers match that search." : "No customers yet."}
                </td></tr>
              )}
              {filtered.map((u) => (
                <tr key={u.id || u.email} onClick={() => u.id && setOpenId(u.id)}
                  className="border-b border-paw-border dark:border-white/5 last:border-0 hover:bg-black/[0.02] dark:hover:bg-white/[0.02] cursor-pointer"
                  data-testid={`customer-row-${u.id || u.email}`}>
                  <td className="px-5 py-4 font-manrope text-sm capitalize">{`${u.first_name || ""} ${u.last_name || ""}`.trim() || "—"}</td>
                  <td className="px-5 py-4 font-manrope text-sm text-paw-text-secondary dark:text-white/70">{u.email}</td>
                  <td className="px-5 py-4 font-manrope text-sm text-paw-text-secondary dark:text-white/60">{u.phone || "—"}</td>
                  <td className="px-5 py-4 font-manrope text-xs">
                    {u.is_active === false ? <span className="text-red-700 dark:text-red-400">Disabled</span> : <span className="text-emerald-700 dark:text-emerald-400">Active</span>}
                  </td>
                  <td className="px-5 py-4 font-manrope text-xs text-paw-text-secondary dark:text-white/60">{day(u.created_at)}</td>
                  <td className="px-5 py-4 text-right">
                    <span className="inline-flex items-center gap-1.5 font-manrope text-xs font-semibold text-paw-accent">
                      <Package className="w-3.5 h-3.5" /> Open
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openId && <CustomerDrawer userId={openId} onClose={closeDrawer} />}
    </div>
  );
}
